import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../../../hooks/hooks';
import { Lx } from '../../../../../i18n/consts';
import FormDateSelector from '../../../../../components/shared/nextgen/form/form-date-selector';
import { FormInput } from '../../../../../components/shared/form-controls/form-input';
import { FormNumberInput } from '../../../../../components/shared/nextgen/form/form-number-input';
import MultipleFileUploader from '../multiple-file-uploader';
import { Employee } from '../../../../../features/employees/types';
import { getEmployee } from '../../../../../features/employees';
import { Cost, CostToEdit, FileMetadata, MAX_COST_AMOUNT, MIN_COST_AMOUNT } from '../../../../../features/cost-report/types';
import { updateCost } from '../../../../../features/cost-report/slice';
import { IsValidExtension, ObjectsToFormDataCoverter } from '../helpers';
import BaseForm from '../../../../../components/shared/form-controls/base-form';
import { NAVIGATION_ROUTES } from '../../../../../routes/NavigationRoute';
import { DropdownField } from '../../../../../components/shared/nextgen/dropdown-field';
import { REmployees } from '../../../../../features/roles/types';
import { PIdNameModel } from '../../../../../features/projects/types';
import { canManageOtherCosts, canSeeCostReportPage } from '../../../../../features/profile/entityPermissions';

interface EditCostState {
    cost?: Cost;
}

interface EditForm {
    employee: REmployees;
    date: string;
    project?: PIdNameModel | null;
    note?: string;
    amount: number;
    files?: File[];
    existingFiles?: FileMetadata[];
}

const DEFAULT_EMPLOYEE: Employee = {
    admin: false,
    canEditAdminLabel: false,
    canEditProjects: false,
    canEditRoles: false,
    email: '',
    firstName: '',
    id: 0,
    isActive: false,
    lastName: '',
    phoneNumber: '',
    position: '',
    projects: [],
    roles: [],
    workCalendar: undefined,
};

const EditCostForm: React.FC = () => {
    const dispatch = useAppDispatch();
    const intl = useIntl();
    const navigate = useNavigate();
    const location = useLocation();
    const profile = useAppSelector((selector) => selector.profile);
    const state = location.state as EditCostState | undefined;
    const editableCost = state?.cost;
    const canViewCostReport = canSeeCostReportPage(profile);
    const canEditOtherCosts = canManageOtherCosts(profile);
    const minEditableDate = useMemo(
        () => canEditOtherCosts ? undefined : moment().startOf('month'),
        [canEditOtherCosts]
    );
    const maxEditableDate = useMemo(
        () => canEditOtherCosts ? undefined : moment().startOf('day'),
        [canEditOtherCosts]
    );

    const [employee, setEmployee] = useState<Employee>(DEFAULT_EMPLOYEE);
    const [removedFileIds, setRemovedFiles] = useState<number[]>([]);
    const hasEmployee = employee.id > 0;

    useEffect(() => {
        if (!canViewCostReport || !editableCost) {
            navigate(NAVIGATION_ROUTES.COSTREPORT, { replace: true });
            return;
        }

        if (!canEditOtherCosts && editableCost.employeeId !== profile.id) {
            navigate(NAVIGATION_ROUTES.COSTREPORT, { replace: true });
            return;
        }

        dispatch(getEmployee(editableCost.employeeId))
            .unwrap()
            .then((value) => setEmployee(value))
            .catch(() => navigate(NAVIGATION_ROUTES.COSTREPORT, { replace: true }));
    }, [canEditOtherCosts, canViewCostReport, dispatch, editableCost, navigate, profile.id]);

    const handleRemovedFiles = (value: FileMetadata[]) => {
        setRemovedFiles(value.map(item => item.id));
    };

    const employeeDisplayName = useMemo(() => {
        if (!hasEmployee) {
            return '';
        }
        return `${employee.firstName} ${employee.lastName}`.trim();
    }, [employee.firstName, employee.lastName, hasEmployee]);

    if (!editableCost || !hasEmployee) {
        return null;
    }

    const employeeOption: REmployees = {
        id: employee.id,
        name: employeeDisplayName || intl.formatMessage({ id: Lx.General.EMPLOYEE }),
        isDefault: true
    };

    const selectedProject = employee.projects.find(project => project.id === (editableCost.projectId ?? 0)) ?? null;

    const initialValues: EditForm = {
        employee: employeeOption,
        date: editableCost.date,
        project: selectedProject,
        note: editableCost.note ?? '',
        amount: editableCost.amount,
        existingFiles: editableCost.files,
        files: []
    };

    const validationScheme: Yup.Schema<EditForm> = Yup.object().shape({
        employee: Yup.object().shape({
            id: Yup.number().required(),
            name: Yup.string().required(),
            isDefault: Yup.boolean().required()
        }).nullable().required(),
        date: Yup.string()
            .required()
            .test('min-date', '', (value) => {
                if (!value || !minEditableDate) {
                    return true;
                }

                return !moment(value, 'YYYY-MM-DD', true).isBefore(minEditableDate, 'day');
            })
            .test('max-date', '', (value) => {
                if (!value || !maxEditableDate) {
                    return true;
                }

                return !moment(value, 'YYYY-MM-DD', true).isAfter(maxEditableDate, 'day');
            }),
        project: Yup.object().shape({
            id: Yup.number().required(),
            name: Yup.string().required(),
            isDefault: Yup.boolean().required()
        }).nullable().optional(),
        amount: Yup.number().required().max(MAX_COST_AMOUNT).min(MIN_COST_AMOUNT),
        note: Yup.string().optional(),
        files: Yup.array().optional()
            .test(
                'max-files',
                intl.formatMessage({ id: Lx.Cost.INVALID_FILES_COUNT }),
                (value) => {
                    return !value || (Array.isArray(value) && value.length <= 10)
                }
            )
            .test(
                'file-types',
                intl.formatMessage({ id: Lx.Cost.INVALID_FILES_EXTENSION }),
                (value) => {
                    if (!value) {
                        return true;
                    }

                    return Array.isArray(value) && value.every(file => IsValidExtension(file));
                }
            ),
        existingFiles: Yup.array().optional()
    });

    const handleSubmit = async (values: EditForm) => {
        if (!canEditOtherCosts && editableCost.employeeId !== profile.id) {
            return;
        }

        const selectedProjectId = values.project?.id ?? editableCost.projectId ?? undefined;
        const cost: CostToEdit = {
            id: editableCost.id,
            employeeId: editableCost.employeeId,
            date: values.date,
            note: values.note,
            amount: values.amount,
            projectId: selectedProjectId,
            files: values.files,
            removedFileIds: removedFileIds
        };

        await dispatch(updateCost({ costId: cost.id, cost: ObjectsToFormDataCoverter(cost) })).unwrap();
    };

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={validationScheme}
            validateOnBlur={true}
            validateOnChange={true}
            onSubmit={handleSubmit}>
            <Form>
                <BaseForm
                    contentGap={24}
                    formType='edit'
                    formHeader={Lx.Cost.EDIT}
                    elementName={employeeDisplayName}
                    savingTitle={intl.formatMessage({ id: Lx.General.COST })}
                    onClose={() => navigate(NAVIGATION_ROUTES.COSTREPORT)}
                    onAfterSave={() => navigate(NAVIGATION_ROUTES.COSTREPORT)}>
                    {canEditOtherCosts && (
                        <div style={{ width: '100%', maxWidth: '504px' }}>
                            <DropdownField
                                label={intl.formatMessage({ id: Lx.General.EMPLOYEE })}
                                name={'employee'}
                                items={[employeeOption]}
                                required
                                placeholder={intl.formatMessage({ id: Lx.General.CHOOSE_EMPLOYEE })} />
                        </div>
                    )}
                    <div style={{ width: '100%', maxWidth: '504px' }}>
                        <FormDateSelector
                            label={intl.formatMessage({ id: Lx.General.DATE })}
                            name={'date'}
                            minDate={minEditableDate}
                            maxDate={maxEditableDate}
                            required />
                    </div>
                    <FormNumberInput
                        max={MAX_COST_AMOUNT}
                        min={MIN_COST_AMOUNT}
                        step={1}
                        label={intl.formatMessage({ id: Lx.Cost.AMOUNT })}
                        name={'amount'}
                        placeholder={intl.formatMessage({ id: Lx.Cost.AMOUNT })}
                        required
                        style={{ width: '100%', maxWidth: '504px' }}
                    />
                    <div style={{ width: '100%', maxWidth: '504px' }}>
                        <DropdownField
                            label={intl.formatMessage({ id: Lx.General.PROJECT })}
                            name={'project'}
                            items={employee.projects}
                            required={false}
                            placeholder={intl.formatMessage({ id: Lx.General.PROJECT })} />
                    </div>
                    <FormInput
                        label={intl.formatMessage({ id: Lx.Cost.NOTE })}
                        name={'note'}
                        placeholder={intl.formatMessage({ id: Lx.Cost.NOTE })}
                        style={{ width: '100%', maxWidth: '504px' }}
                    />
                    <div style={{ width: '100%', maxWidth: '504px' }}>
                        <MultipleFileUploader
                            label={intl.formatMessage({ id: Lx.Cost.UPLOAD_FILES })}
                            hint={intl.formatMessage({ id: Lx.Cost.UPLOAD_FILES_HINT })}
                            onRemovedFiles={handleRemovedFiles}
                            initialValues={editableCost.files}
                            existingFilesFieldName='existingFiles'
                            name={'files'}
                        />
                    </div>
                </BaseForm>
            </Form>
        </Formik>
    );
};

export default EditCostForm;
